import * as THREE from "three";
import { fetchAsset, ensureAsset } from "../core/AssetSource.js";
import { fetchApiJson } from "../core/ApiData.js";
import { SHOW_DEV_TOOLS, SHOW_UNRELEASED_CATALOG, assetUrl } from "../config.js";
import {
  ENABLE_ANISOTROPY_MAPS,
  applyNapShading,
  configureAnisotropyMap,
} from "./AnisotropyMap.js";

// Roughly 10MB of GPU memory each once mipmapped, so this is the ceiling that
// keeps a long browsing session off the jetsam threshold.
const MAX_CACHED_FABRICS = 12;

export class MaterialManager {
  constructor(loadingManager, renderer, app = null) {
    this.loadingManager = loadingManager;
    this.renderer = renderer;
    this.app = app;
    this.fabrics = {};
    this.fabricGroups = {};
    this.devOnlyGroups = []; // cleaned keys of dev-only (`__`) fabric groups
    this.loadedTextures = {};
    this._pendingLoads = new Map(); // In-flight deduplication for loadFabricTexture
    this.currentQuality = "medium";
    this.textureLoader = new THREE.TextureLoader(this.loadingManager);
    this.productFabrics = {}; // Maps product urlCode to allowed fabric groups
    this.productColors = {}; // Maps product urlCode to ordered fabric codes
    this.productOrdering = {}; // Maps set/combination ID to ordered fabric codes
    this.thumbnailCache = {}; // Cache generated thumbnail data URLs
  }

  // ===== INITIALIZATION =====
  async init() {
    await this.loadMaterialsData();
    // Textures are now loaded on-demand when materials are applied
  }

  async loadMaterialsData() {
    try {
      // Load materials data from JSON file
      const response = await fetchAsset("data/materials.json");
      if (!response.ok) {
        throw new Error(
          `Failed to load materials data: ${response.statusText}`,
        );
      }
      const materialsData = await response.json();

      // Editor-only gating data; loads in the background (not awaited) so first paint never waits
      // on the backend. Consumers null-guard, UIManager re-filters on resolve. Embed mode skips it.
      this.productDataReady = this.app?.isEmbedMode
        ? Promise.resolve()
        : Promise.all([this.loadProductFabrics(), this.loadProductColors(), this.loadProductOrdering()]);

      // Group key prefixes (mirrors the convention in furniture.json):
      //   `__` prefix = dev-only (visible in dev, hidden in prod).
      //   single `_` prefix = always hidden (disabled).
      // The prefix is stripped from the key so texture paths
      // (`textures/fabric/<group>/`), IDs and getFabricGroups() stay clean.
      this.fabricGroups = {};
      this.devOnlyGroups = []; // cleaned keys of `__`-prefixed groups (dev only)
      for (const [rawKey, group] of Object.entries(materialsData.groups || {})) {
        if (rawKey.startsWith("__")) {
          if (!SHOW_UNRELEASED_CATALOG) {
            if (SHOW_DEV_TOOLS) console.log(`Skipping dev-only fabric group: ${rawKey}`);
            continue;
          }
          const cleanKey = rawKey.slice(2);
          this.fabricGroups[cleanKey] = group;
          this.devOnlyGroups.push(cleanKey);
        } else if (rawKey.startsWith("_")) {
          if (SHOW_DEV_TOOLS) console.log(`Skipping disabled fabric group: ${rawKey}`);
          continue;
        } else {
          this.fabricGroups[rawKey] = group;
        }
      }
      this.materialSettings = materialsData.settings || {};
      this.textureBasePath =
        this.materialSettings.textureBasePath || "models/textures/";
      this.defaultProperties = this.materialSettings.defaultProperties || {
        roughness: 0.8,
        metalness: 0.0,
        repeat: [1, 1],
      };
      this.materialZoneProperties =
        this.materialSettings.materialZoneProperties || {};
      // Per-group dark-fabric override (group key → fabric code in same group).
      this.darkFabricByGroup =
        this.materialSettings.darkFabricByGroup || {};

      // Convert to individual fabric entries
      let fabricId = 1;
      for (const [groupKey, group] of Object.entries(this.fabricGroups)) {
        // Skip groups with no textures
        if (!group.textures || group.textures.length === 0) {
          continue;
        }
        for (const texture of group.textures) {
          if (!texture || texture.trim() === "") {
            continue;
          }
          const id = `fabric_${String(fabricId).padStart(3, "0")}`;
          const fabricCode = this.extractFabricCode(texture);
          const name = this.getFabricName(fabricCode, group.fabricNames);
          const color = this.extractColorFromFilename(texture);

          // Per-color PBR maps (heim/hygge) derive their normal/roughness/AO
          // paths from the diffuse filename: `fabric-1k_HM-03.jpg` ->
          // `map-1k_HM-03_<type>.jpg`. Legacy groups instead share a single
          // `normalMap` for every color in the group.
          const mapDir = `textures/fabric/${groupKey}`;
          const perColorPath = (mapType) =>
            group.perColorMaps
              ? `${mapDir}/${this.deriveMapFilename(texture, mapType)}`
              : null;
          const normalPath =
            perColorPath("normal") ??
            (group.normalMap ? `${mapDir}/${group.normalMap}` : null);
          // anisotropyMap filenames stay in materials.json; ENABLE_ANISOTROPY_MAPS
          // gates load/apply so we can turn nap maps back on without recataloguing.
          const anisotropyPath =
            SHOW_DEV_TOOLS && ENABLE_ANISOTROPY_MAPS && group.anisotropyMap
              ? `${mapDir}/${group.anisotropyMap}`
              : null;
          // const roughnessPath = perColorPath("roughness");
          // const aoPath = perColorPath("ambientOcclusion");

          this.fabrics[id] = {
            id,
            name,
            fabricCode,
            group: groupKey,
            groupName: group.name,
            type: group.type,
            color,
            texture: texture,
            path: `${mapDir}/${texture}`,
            normalPath,
            anisotropyPath,
            // roughnessPath,
            // aoPath,
            properties: {
              ...this.defaultProperties,
              ...(group.properties || {}),
            },
          };
          fabricId++;
        }
      }
    } catch (error) {
      console.error("Error loading materials data:", error);
      console.error("Fetch response details:", error.message);
      // Fallback to empty groups
      this.fabricGroups = {};
    }
  }

  async loadProductFabrics() {
    const data = await fetchApiJson('product-fabrics', { snapshot: 'product-fabrics.json' });
    if (!data) {
      console.warn('No product-fabrics data available, all fabrics will be available');
      this.productFabrics = {};
      return;
    }

    // Prefer country-specific mapping;
    // fall back to GLOBAL when the country slice is missing.
    const country = this.app?.getLocalizationManager()?.country;
    this.productFabrics = (country && data[country]) || data.GLOBAL || {};
  }

  async loadProductColors() {
    const country = this.app?.getLocalizationManager()?.country || 'GLOBAL';
    const data = await fetchApiJson('product-colors', {
      params: { country },
      snapshot: 'product-colors.json',
    });
    if (!data) console.warn('No product-colors data available, default ordering will be used');
    this.productColors = data || {};
  }

  async loadProductOrdering() {
    const data = await fetchApiJson('product-ordering', { snapshot: 'product-ordering.json' });
    if (!data) console.warn('No product-ordering data available, thumbnails will use default fabric');

    // Use GLOBAL mapping
    this.productOrdering = data?.GLOBAL || {};
  }

  getOrderedFabricCodes(urlCode) {
    if (!urlCode) return null;

    const orderedCodes = this.productColors[urlCode];
    if (!orderedCodes || orderedCodes.length === 0) return null;

    // The backend product-colors ordering won't know about dev-only groups, so
    // in dev append their fabric codes — otherwise the UI's "filter to ordered
    // codes" step (UIManager.updateMaterialGrid) drops dev-only swatches.
    if (SHOW_UNRELEASED_CATALOG && this.devOnlyGroups?.length) {
      const devCodes = this.getDevOnlyFabricCodes();
      if (devCodes.length) return [...orderedCodes, ...devCodes];
    }

    return orderedCodes;
  }

  // Fabric codes belonging to dev-only (`__`) groups. Empty unless SHOW_UNRELEASED_CATALOG,
  // since dev-only groups aren't loaded in production.
  getDevOnlyFabricCodes() {
    if (!this.devOnlyGroups?.length) return [];
    return Object.values(this.fabrics)
      .filter(f => this.devOnlyGroups.includes(f.group))
      .map(f => f.fabricCode);
  }

  getAllowedFabricGroups(urlCode) {
    if (!urlCode) return null;

    // Check if we have restrictions for this product
    const allowedGroups = this.productFabrics[urlCode];

    if (!allowedGroups || allowedGroups.length === 0) {
      // No restrictions found, allow all (dev-only groups already loaded only in dev)
      return null;
    }

    // The backend product-fabrics allowlist won't know about dev-only groups,
    // so in dev append them to every product's allowlist so they stay visible.
    if (SHOW_UNRELEASED_CATALOG && this.devOnlyGroups?.length) {
      return [...allowedGroups, ...this.devOnlyGroups];
    }

    return allowedGroups;
  }

  /**
   * The first fabric a product can wear, in catalog order — the fallback when a preferred fabric
   * is unsold here (country filtering keeps it out of `fabrics`) or blocked by the allowlist.
   * @param {string|null} [urlCode] product to respect the ordering/allowlist of; null = any
   * @returns {string|null} fabric id, or null when nothing is available at all
   */
  getFirstAvailableFabricId(urlCode = null) {
    // Prefer the backend's per-product colour order, so "first" matches the picker's first swatch.
    for (const code of this.getOrderedFabricCodes(urlCode) || []) {
      const id = this.getFabricIdByCode(code);
      if (id && this.isFabricAllowedForProduct(id, urlCode)) return id;
    }

    // No ordering for this product, or nothing in it survived the country filter.
    for (const id of Object.keys(this.fabrics)) {
      if (this.isFabricAllowedForProduct(id, urlCode)) return id;
    }
    return null;
  }

  /**
   * `fabricId` if this product may wear it, else the product's first available colour.
   * The picker's colour is scene-wide but `product-fabrics` is per product, and the shared
   * accessories wear fewer groups than the modules they sit beside — the backend prices an
   * unsold combination at 0, so paint must be resolved per item before it lands.
   * @param {string} fabricId
   * @param {string|null} urlCode
   * @returns {string|null} null when the product can wear nothing at all
   */
  resolveFabricForProduct(fabricId, urlCode) {
    if (!fabricId) return null;
    if (this.isFabricAllowedForProduct(fabricId, urlCode)) return fabricId;
    return this.getFirstAvailableFabricId(urlCode);
  }

  isFabricAllowedForProduct(fabricId, urlCode) {
    const allowedGroups = this.getAllowedFabricGroups(urlCode);

    // If no restrictions, all fabrics are allowed
    if (!allowedGroups) return true;

    // Get the fabric's group
    const fabric = this.fabrics[fabricId];
    if (!fabric) return false;

    // Check if fabric's group is in the allowed list
    return allowedGroups.includes(fabric.group);
  }

  // Rewrite a texture path's resolution by swapping the single "-1k" token (e.g. fabric-1k_ET-14.jpg -> fabric-512_ET-14.jpg); "1k" is unchanged. Group keys/fabric codes never contain "-1k", so the swap is unambiguous.
  toResolution(path, resolution) {
    if (!path || resolution === "1k") return path;
    return path.replace("-1k", `-${resolution}`);
  }

  // Derive a per-color PBR map filename from the diffuse texture filename.
  // e.g. ("fabric-1k_HM-03.jpg", "normal") -> "map-1k_HM-03_normal.jpg"
  deriveMapFilename(diffuseFilename, mapType) {
    const base = diffuseFilename
      .replace(/^fabric-/, "")
      .replace(/\.[^.]+$/, "");
    return `map-${base}_${mapType}.jpg`;
  }

  extractFabricCode(filename) {
    // Extract fabric code from filename (e.g., "fabric-1k_ET-14.jpg" -> "ET-14")
    const parts = filename.split("_");
    if (parts.length > 1) {
      const code = parts[1].split(".")[0];
      return code;
    }
    return null;
  }

  getFabricName(fabricCode, fabricNames) {
    // Look up fabric name from fabricNames mapping
    if (fabricNames && fabricCode && fabricNames[fabricCode]) {
      return fabricNames[fabricCode];
    }
    // Fallback to fabric code if name not found
    return fabricCode || "Unknown";
  }

  extractColorFromFilename(filename) {
    // Generate color based on filename hash for consistency
    let hash = 0;
    for (let i = 0; i < filename.length; i++) {
      hash = filename.charCodeAt(i) + ((hash << 5) - hash);
    }

    const hue = Math.abs(hash) % 360;
    const saturation = 20 + (Math.abs(hash) % 30); // 20-50%
    const lightness = 40 + (Math.abs(hash) % 20); // 40-60%

    return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
  }

  async loadFabricTexture(fabric, resolution = "512") {
    const cacheKey = `${fabric.id}_${resolution}`;

    if (this.loadedTextures[cacheKey]) {
      return this.loadedTextures[cacheKey];
    }

    // Deduplicate concurrent requests for the same fabric — return the same
    // in-flight promise so rapid clicks don't spawn multiple texture loads.
    if (this._pendingLoads.has(cacheKey)) {
      return this._pendingLoads.get(cacheKey);
    }

    if (!fabric.path || fabric.path.trim() === "") {
      console.warn("Empty fabric path detected:", fabric);
      return this.createFallbackTexture(fabric);
    }

    const loadPromise = this._doLoadFabricTexture(fabric, cacheKey, resolution);
    // Keep in map until settled; the .finally removes it so subsequent calls
    // hit this.loadedTextures instead.
    this._pendingLoads.set(
      cacheKey,
      loadPromise.finally(() => this._pendingLoads.delete(cacheKey)),
    );
    return loadPromise;
  }

  async _doLoadFabricTexture(fabric, cacheKey, resolution = "512") {
    try {
      const texture = await this.loadTexture(
        this.toResolution(fabric.path, resolution),
      );

      // Configure texture for proper color reproduction
      texture.wrapS = THREE.RepeatWrapping;
      texture.wrapT = THREE.RepeatWrapping;
      // Use repeat values from fabric properties
      const repeatX = fabric.properties?.repeat?.[0] || 1;
      const repeatY = fabric.properties?.repeat?.[1] || 1;
      texture.repeat.set(repeatX, repeatY);
      texture.colorSpace = THREE.SRGBColorSpace;
      texture.flipY = false;
      texture.generateMipmaps = true;
      texture.minFilter = THREE.LinearMipmapLinearFilter;
      texture.magFilter = THREE.LinearFilter;
      // Enable anisotropic filtering for sharper textures at oblique angles
      texture.anisotropy = this.renderer.capabilities.getMaxAnisotropy();

      // Load supplementary PBR maps if available. Normal maps exist for every
      // group; roughness/AO maps only ship with per-color groups (heim/hygge).
      // These are data maps (not color), so they stay in linear space — the
      // TextureLoader default — unlike the sRGB diffuse `texture` above.
      const loadDataMap = async (path, label, { directionField = false } = {}) => {
        if (!path) return null;
        try {
          const map = await this.loadTexture(path);
          map.wrapS = THREE.RepeatWrapping;
          map.wrapT = THREE.RepeatWrapping;
          map.repeat.set(repeatX, repeatY);
          map.flipY = false;
          map.anisotropy = this.renderer.capabilities.getMaxAnisotropy();
          if (directionField) configureAnisotropyMap(map);
          return map;
        } catch (error) {
          console.warn(`Failed to load ${label} for ${fabric.name}:`, error);
          return null;
        }
      };

      // Prefer the requested resolution; fall back to the 1k original when a 512
      // variant has not been generated yet.
      const loadResolved = async (path, label, opts) => {
        if (!path) return null;
        const resolved = this.toResolution(path, resolution);
        return (
          (await loadDataMap(resolved, label, opts)) ??
          (resolved !== path ? loadDataMap(path, label, opts) : null)
        );
      };

      // const [normalMap, roughnessMap, aoMap] = await Promise.all([
      //   loadDataMap(fabric.normalPath, "normal map"),
      //   loadDataMap(fabric.roughnessPath, "roughness map"),
      //   loadDataMap(fabric.aoPath, "ambient occlusion map"),
      // ]);

      const [normalMap, anisotropyMap] = await Promise.all([
        loadResolved(fabric.normalPath, "normal map"),
        // Skipped while ENABLE_ANISOTROPY_MAPS is false (anisotropyPath is null).
        loadResolved(fabric.anisotropyPath, "anisotropy map", { directionField: true }),
      ]);

      const fabricData = {
        id: fabric.id,
        name: fabric.name,
        fabricCode: fabric.fabricCode,
        group: fabric.group,
        type: fabric.type,
        color: fabric.color,
        texture,
        normalMap,
        anisotropyMap,
        // roughnessMap,
        // aoMap,
        properties: fabric.properties,
        price: fabric.price,
      };

      this.loadedTextures[cacheKey] = fabricData;
      this._evictUnusedTextures();
      return fabricData;
    } catch (error) {
      console.error(`Failed to load fabric texture ${fabric.name}:`, error);

      // Return fallback texture
      return this.createFallbackTexture(fabric);
    }
  }

  // Warm the browser image cache for a fabric's map URLs (no THREE texture, no `_pendingLoads`, so
  // the 1k upgrade isn't starved); URLs match TextureLoader so a later load reads cache. Deduped per URL.
  prefetchFabricMaps(fabricId, { resolution = "512", maps = ["diffuse", "normal"] } = {}) {
    const fabric = this.fabrics?.[fabricId];
    if (!fabric) return;
    if (!this._warmedMapUrls) this._warmedMapUrls = new Set();

    const urls = [];
    if (maps.includes("diffuse")) urls.push(this.toResolution(fabric.path, resolution));
    if (maps.includes("normal")) urls.push(this.toResolution(fabric.normalPath, resolution));
    if (maps.includes("anisotropy") && fabric.anisotropyPath) {
      urls.push(this.toResolution(fabric.anisotropyPath, resolution));
    }

    for (const url of urls) {
      if (!url || !url.trim() || this._warmedMapUrls.has(url)) continue;
      this._warmedMapUrls.add(url);
      const img = new Image();
      img.decoding = "async";
      if ("fetchPriority" in img) img.fetchPriority = "low";
      img.src = url; // raw relative path — same URL TextureLoader requests, so the fetch is shared
    }
  }

  async loadTexture(path) {
    if (!path || path.trim() === "" || path.endsWith("/")) {
      console.warn("Invalid texture path:", path);
      throw new Error(`Invalid texture path: ${path}`);
    }

    // Fabric maps stream on first use; the loader then reads the cached local file.
    await ensureAsset(path);

    return new Promise((resolve, reject) => {
      this.textureLoader.load(
        path,
        (texture) => resolve(texture),
        (progress) => {
          // Loading progress
        },
        (error) => {
          console.warn(`Failed to load texture: ${path}`, error);
          reject(error);
        },
      );
    });
  }

  createFallbackTexture(fabric) {
    const canvas = this.createFallbackCanvas();
    const context = canvas.getContext("2d");

    if (fabric.type === "leather") {
      this.drawLeatherTexture(context, canvas.width, canvas.height);
    } else {
      this.drawFabricTexture(context, canvas.width, canvas.height, fabric.color);
    }

    const texture = this.createCanvasTexture(canvas);

    return {
      id: fabric.id,
      name: fabric.name,
      fabricCode: fabric.fabricCode,
      group: fabric.group,
      type: fabric.type,
      color: fabric.color,
      texture,
      normalMap: null,
      anisotropyMap: null,
      price: fabric.price,
    };
  }

  createFallbackCanvas() {
    const canvas = document.createElement("canvas");
    canvas.width = 512;
    canvas.height = 512;
    return canvas;
  }

  drawLeatherTexture(context, width, height) {
    const LEATHER_BASE_COLOR = "#8B4513";
    const LEATHER_NOISE_COUNT = 1000;
    const LEATHER_NOISE_SIZE = 2;
    const LEATHER_NOISE_OPACITY_MAX = 0.3;

    // Fill with base leather color
    context.fillStyle = LEATHER_BASE_COLOR;
    context.fillRect(0, 0, width, height);

    // Add noise for texture
    for (let i = 0; i < LEATHER_NOISE_COUNT; i++) {
      const x = Math.random() * width;
      const y = Math.random() * height;
      const opacity = Math.random() * LEATHER_NOISE_OPACITY_MAX;
      context.fillStyle = `rgba(139, 69, 19, ${opacity})`;
      context.fillRect(x, y, LEATHER_NOISE_SIZE, LEATHER_NOISE_SIZE);
    }
  }

  drawFabricTexture(context, width, height, color) {
    const WEAVE_SPACING = 8;
    const WEAVE_LINE_WIDTH = 1;
    const GRADIENT_LIGHTNESS_DELTA = -20;
    const WEAVE_COLOR_LIGHTNESS_DELTA = -10;

    // Create gradient background
    const gradient = context.createLinearGradient(0, 0, width, height);
    gradient.addColorStop(0, color);
    gradient.addColorStop(1, this.adjustColor(color, GRADIENT_LIGHTNESS_DELTA));
    context.fillStyle = gradient;
    context.fillRect(0, 0, width, height);

    // Add fabric weave pattern
    context.strokeStyle = this.adjustColor(color, WEAVE_COLOR_LIGHTNESS_DELTA);
    context.lineWidth = WEAVE_LINE_WIDTH;

    for (let i = 0; i < width; i += WEAVE_SPACING) {
      context.beginPath();
      context.moveTo(i, 0);
      context.lineTo(i, height);
      context.stroke();

      context.beginPath();
      context.moveTo(0, i);
      context.lineTo(width, i);
      context.stroke();
    }
  }

  createCanvasTexture(canvas) {
    const FALLBACK_TEXTURE_REPEAT = 2;

    const texture = new THREE.CanvasTexture(canvas);
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    texture.repeat.set(FALLBACK_TEXTURE_REPEAT, FALLBACK_TEXTURE_REPEAT);
    return texture;
  }

  adjustColor(hslColor, lightnessDelta) {
    // Parse HSL and adjust lightness
    const match = hslColor.match(/hsl\((\d+),\s*(\d+)%,\s*(\d+)%\)/);
    if (match) {
      const h = parseInt(match[1]);
      const s = parseInt(match[2]);
      const l = Math.max(0, Math.min(100, parseInt(match[3]) + lightnessDelta));
      return `hsl(${h}, ${s}%, ${l}%)`;
    }
    return hslColor;
  }

  // ===== PUBLIC API =====
  // True while any fabric texture load is in flight, so background work (the 1k upgrade) can hold off instead of competing for bandwidth.
  hasPendingLoads() {
    return this._pendingLoads.size > 0;
  }

  // Defaults to the lightweight 512 set so the scene paints fast; pass "1k" for the active fabric's background upgrade.
  async getFabricData(fabricId, resolution = "512") {
    const fabric = this.fabrics[fabricId];
    if (!fabric) {
      console.warn(`Fabric not found: ${fabricId}`);
      return null;
    }

    return await this.loadFabricTexture(fabric, resolution);
  }

  // Clone a freshly-loaded map for an in-place resolution swap, matching the current map's tiling/offset so the weave doesn't shift. `srgb` for diffuse; false (linear) for normal maps.
  cloneMapMatching(sourceMap, refMap, { srgb = false } = {}) {
    if (!sourceMap) return null;
    const m = sourceMap.clone();
    m.wrapS = THREE.RepeatWrapping;
    m.wrapT = THREE.RepeatWrapping;
    if (refMap) {
      m.repeat.copy(refMap.repeat);
      m.offset.copy(refMap.offset);
    }
    m.flipY = false;
    if (srgb) m.colorSpace = THREE.SRGBColorSpace;
    m.anisotropy = this.renderer.capabilities.getMaxAnisotropy();
    m.needsUpdate = true;
    return m;
  }

  getFabricGroups() {
    return this.fabricGroups;
  }

  getFabricsInGroup(groupKey) {
    return Object.values(this.fabrics).filter(
      (fabric) => fabric.group === groupKey,
    );
  }

  getFabricIdByCode(fabricCode) {
    for (const [id, fabric] of Object.entries(this.fabrics)) {
      if (fabric.fabricCode === fabricCode) {
        return id;
      }
    }
    return null;
  }

  getDarkFabricIdForGroup(groupKey) {
    const code = this.darkFabricByGroup[groupKey];
    return code ? this.getFabricIdByCode(code) : null;
  }

  // Create material with fabric using Three.js best practices
  createMaterialWithFabric(
    fabricData,
    materialType = "MeshStandardMaterial",
    materialZone = "fabric",
  ) {
    if (!fabricData) {
      console.warn("createMaterialWithFabric called with null fabricData");
      return null;
    }

    // Clone textures to avoid shared state issues
    let texture = null;
    let normalMap = null;
    // let roughnessMap = null;
    // let aoMap = null;

    // Get zone-specific properties, falling back to fabric properties, then defaults
    const zoneProperties = this.materialZoneProperties?.[materialZone] || {};
    const fabricProperties = fabricData.properties || {};
    const defaults = this.defaultProperties || {};
    // Per-fabric properties in materials.json take precedence; fall back to the
    // material zone defaults, then the global defaults. This is the point of
    // declaring `properties` on a fabric — they override the zone baseline.
    const pick = (key, fallback) =>
      fabricProperties[key] ?? zoneProperties[key] ?? defaults[key] ?? fallback;
    const roughness = pick("roughness", 0.8);
    const metalness = pick("metalness", 0.0);
    const repeat = pick("repeat", [1, 1]);
    const isLeather = fabricData.type === "leather";
    // Fabric nap is dev-only until it has proper maps; production builds plain standard cloth.
    const anisotropy = SHOW_DEV_TOOLS ? pick("anisotropy", 0) : 0;
    const anisotropyRotation = pick("anisotropyRotation", 0);
    const sheen = pick("sheen", 0);
    const sheenRoughness = pick("sheenRoughness", 1);
    const sheenColor = pick("sheenColor", "#ffffff");
    // Normal map intensity. Accepts a single number (uniform) or [x, y] in
    // materials.json; higher = deeper/sharper weave relief, lower = flatter.
    const normalScaleRaw =
      fabricProperties.normalScale ??
      zoneProperties.normalScale ??
      defaults.normalScale ??
      1.0;
    const normalScale = Array.isArray(normalScaleRaw)
      ? new THREE.Vector2(normalScaleRaw[0], normalScaleRaw[1])
      : new THREE.Vector2(normalScaleRaw, normalScaleRaw);

    // Add a random offset to break up visible tiling. Every map for this
    // fabric shares the same offset so the per-color normal/roughness/AO detail
    // stays registered with the diffuse weave.
    const offsetX = Math.random();
    const offsetY = Math.random();

    // Clone + configure a texture (diffuse or supplementary map) so every map
    // for this material shares the same tiling/offset.
    const cloneMap = (sourceMap) => {
      if (!sourceMap) return null;
      const m = sourceMap.clone();
      m.wrapS = THREE.RepeatWrapping;
      m.wrapT = THREE.RepeatWrapping;
      m.repeat.set(repeat[0], repeat[1]);
      m.offset.set(offsetX, offsetY);
      m.flipY = false;
      m.needsUpdate = true;
      return m;
    };

    texture = cloneMap(fabricData.texture);
    normalMap = cloneMap(fabricData.normalMap);
    // Uniform anisotropy / nap still apply when properties.anisotropy > 0.
    // The tiled nap map is off until ENABLE_ANISOTROPY_MAPS is true.
    const anisotropyMap =
      ENABLE_ANISOTROPY_MAPS && anisotropy > 0
        ? configureAnisotropyMap(cloneMap(fabricData.anisotropyMap))
        : null;
    // roughnessMap = cloneMap(fabricData.roughnessMap);
    // aoMap = cloneMap(fabricData.aoMap);

    if (anisotropy > 0) materialType = "MeshPhysicalMaterial";

    let roughnessMap = null;

    const materialProps = {
      name: fabricData.id + "_fabric",
      // Which fabric this material carries, for path-tracing.json's per-fabric grading
      // (see PathTracingFabricGrading) — the material name is id-based, not code-based.
      userData: { fabricCode: fabricData.fabricCode, fabricGroup: fabricData.group },
      map: texture, // Use the cloned texture
      normalMap: normalMap, // Use the cloned normal map
      normalScale, // fabric → zone → default (materials.json), defaults to 1.0
      // roughnessMap, // Per-color roughness (heim/hygge); null otherwise
      // aoMap, // Per-color ambient occlusion; uses the primary UV set (channel 0)
      color: 0xffffff, // White base for texture
      // When a roughness map is present let it drive roughness fully (the map
      // value is multiplied by this scalar), otherwise use the zone/fabric value.
      roughness: roughnessMap ? 1.0 : roughness,
      metalness: metalness,
    };

    // Create material based on type
    switch (materialType) {
      case "MeshStandardMaterial":
        return new THREE.MeshStandardMaterial({
          ...materialProps,
          side: THREE.DoubleSide,
        });
      case "MeshPhysicalMaterial": {
        const mat = new THREE.MeshPhysicalMaterial({
          ...materialProps,
          side: THREE.DoubleSide,
          anisotropy,
          anisotropyRotation,
          anisotropyMap,
          specularIntensity: anisotropy > 0 ? 0.15 : 1,
          sheen,
          sheenRoughness,
          sheenColor: new THREE.Color(sheenColor),
          clearcoat: isLeather ? 0.2 : 0.0,
          clearcoatRoughness: 0.3,
        });
        if (anisotropy > 0) applyNapShading(mat);
        return mat;
      }
      default:
        return new THREE.MeshLambertMaterial({
          map: texture, // Use the cloned texture
          color: 0xffffff,
        });
    }
  }

  // Generate thumbnail for UI using actual texture
  generateThumbnail(fabricId, size = 128) {
    const fabric = this.fabrics[fabricId];
    if (!fabric) return null;

    // Return cached thumbnail if available
    const thumbKey = `${fabricId}_${size}`;
    if (this.thumbnailCache[thumbKey]) return this.thumbnailCache[thumbKey];

    // Try to get an already-loaded texture (either resolution) for the thumbnail.
    const loadedFabricData =
      this.loadedTextures[`${fabricId}_512`] ||
      this.loadedTextures[`${fabricId}_1k`];

    let result;
    if (
      loadedFabricData &&
      loadedFabricData.texture &&
      loadedFabricData.texture.image
    ) {
      // Use actual texture for thumbnail
      result = this.createTextureBasedThumbnail(loadedFabricData.texture, size, fabricId);
    } else {
      // Fallback to simple color thumbnail while texture loads
      result = this.createColorThumbnail(fabric, size);
    }

    if (result) this.thumbnailCache[thumbKey] = result;
    return result;
  }

  createTextureBasedThumbnail(texture, size, fabricId) {
    // Check cache first when fabricId provided
    if (fabricId) {
      const thumbKey = `${fabricId}_${size}_tex`;
      if (this.thumbnailCache[thumbKey]) return this.thumbnailCache[thumbKey];
    }

    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size;
    const context = canvas.getContext("2d");

    // Draw the actual texture scaled down
    if (texture.image.complete) {
      context.drawImage(texture.image, 0, 0, size, size);
    } else {
      // Image not loaded yet, return null to fallback
      return null;
    }

    const dataUrl = canvas.toDataURL();

    // Cache the result and also update the main thumbnail cache
    if (fabricId) {
      this.thumbnailCache[`${fabricId}_${size}_tex`] = dataUrl;
      this.thumbnailCache[`${fabricId}_${size}`] = dataUrl;
    }

    return dataUrl;
  }

  createColorThumbnail(fabric, size) {
    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size;
    const context = canvas.getContext("2d");

    // Use neutral gray color instead of artificial fabric.color
    const baseColor = "#888888"; // Neutral gray

    // Create simple gradient
    const gradient = context.createLinearGradient(0, 0, size, size);
    gradient.addColorStop(0, baseColor);
    gradient.addColorStop(1, "#666666");
    context.fillStyle = gradient;
    context.fillRect(0, 0, size, size);

    // Add fabric type indicator
    context.fillStyle = "#ffffff";
    context.font = `${size * 0.6}px Arial`;
    context.textAlign = "center";
    context.textBaseline = "middle";

    // Extract fabric code from name (e.g., "ET-14" from "Pattern ET-14")
    const match = fabric.name.match(/([A-Z]{1,3}-\d{1,3})/);
    const code = match ? match[1] : fabric.name.slice(-3);

    context.fillText(code, size / 2, size / 2);

    return canvas.toDataURL();
  }

  // Update method called in main render loop
  update() {
    // Perform any necessary updates for material system
    // Could include texture streaming, LOD management, etc.
  }

  // Get statistics for debugging
  getStats() {
    return {
      totalFabrics: Object.keys(this.fabrics).length,
      loadedTextures: Object.keys(this.loadedTextures).length,
      currentQuality: this.currentQuality,
      memoryUsage: this.calculateMemoryUsage(),
    };
  }

  calculateMemoryUsage() {
    let totalSize = 0;
    Object.values(this.loadedTextures).forEach((fabricData) => {
      if (fabricData.texture && fabricData.texture.image) {
        totalSize +=
          fabricData.texture.image.width * fabricData.texture.image.height * 4; // RGBA
      }
      if (fabricData.normalMap && fabricData.normalMap.image) {
        totalSize +=
          fabricData.normalMap.image.width *
          fabricData.normalMap.image.height *
          4;
      }
      if (fabricData.anisotropyMap && fabricData.anisotropyMap.image) {
        totalSize +=
          fabricData.anisotropyMap.image.width *
          fabricData.anisotropyMap.image.height *
          4;
      }
    });
    return Math.round(totalSize / (1024 * 1024)); // MB
  }

  // Dispose of all textures to prevent memory leaks
  dispose() {
    Object.values(this.loadedTextures).forEach((fabricData) => {
      if (fabricData.texture) fabricData.texture.dispose();
      if (fabricData.normalMap) fabricData.normalMap.dispose();
      if (fabricData.anisotropyMap) fabricData.anisotropyMap.dispose();
      // if (fabricData.roughnessMap) fabricData.roughnessMap.dispose();
      // if (fabricData.aoMap) fabricData.aoMap.dispose();
    });
    this.loadedTextures = {};
  }

  /**
   * Cap the fabric texture cache.
   *
   * Each entry holds a 1k diffuse and normal map — roughly 10MB of GPU memory once
   * mipmapped — and browsing the swatch grid adds one per fabric with nothing ever
   * releasing them. Twenty fabrics is enough to get the app killed on iOS.
   *
   * Only textures no element is currently wearing are evicted; disposing one still bound
   * to a visible material would blank the sofa.
   */
  _evictUnusedTextures(limit = MAX_CACHED_FABRICS) {
    const keys = Object.keys(this.loadedTextures);
    if (keys.length <= limit) return;

    const inUse = new Set();
    for (const element of this.app?.getFurnitureManager?.()?.getAllFurniture?.() ?? []) {
      if (element.fabricId) inUse.add(element.fabricId);
    }
    if (this.selectedMaterial) inUse.add(this.selectedMaterial);

    // Object key order is insertion order, so the front is the least recently added.
    for (const key of keys) {
      if (Object.keys(this.loadedTextures).length <= limit) break;
      const fabricId = key.slice(0, key.lastIndexOf("_"));
      if (inUse.has(fabricId)) continue;

      const entry = this.loadedTextures[key];
      entry?.texture?.dispose();
      entry?.normalMap?.dispose();
      entry?.anisotropyMap?.dispose();
      delete this.loadedTextures[key];
    }
  }

}
